import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Undo2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

/**
 * AdminScheduleManager
 *
 * Edits the /schedule (Order of Events) page: eyebrow + title, plus a
 * CRUD-and-reorder list of items each with time, event, and description.
 */
const AdminScheduleManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/schedule/content`);
      setInitial(clone(res.data || {}));
      setContent(clone(res.data || {}));
    } catch { setError('Could not load current content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => (
    content && initial && JSON.stringify(content) !== JSON.stringify(initial)
  ), [content, initial]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/admin/schedule/content`, content, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/schedule/content/reset`, {}, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  if (loading || !content) {
    return <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading Schedule…
    </div>;
  }

  const items = content.items || [];
  const setItem = (i, key, val) => patch((d) => { d.items[i][key] = val; });
  const move = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.items.length) return;
    [d.items[i], d.items[j]] = [d.items[j], d.items[i]];
  });
  const remove = (i) => patch((d) => { d.items.splice(i, 1); });
  const add = () => patch((d) => { d.items = d.items || []; d.items.push({ time: '', event: 'New Event', description: '' }); });

  return (
    <div className="space-y-4" data-testid="admin-schedule">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-[#3B2F2F]">Order of Events</h2>
          <p className="text-xs text-[#8A7A7A]">Edits go live on the public /schedule page.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="schedule-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={() => setContent(clone(initial))} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="schedule-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="schedule-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Eyebrow (script line)</label>
            <Input value={content.eyebrow || ''} onChange={(e) => patch((d) => { d.eyebrow = e.target.value; })} data-testid="schedule-eyebrow" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
            <Input value={content.title || ''} onChange={(e) => patch((d) => { d.title = e.target.value; })} data-testid="schedule-title" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-[#3B2F2F]">Timeline Items</h3>
            <p className="text-xs text-[#8A7A7A]">The public timeline renders these in order.</p>
          </div>
          <Button onClick={add} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="schedule-add">
            <Plus className="w-4 h-4 mr-1" /> Add item
          </Button>
        </div>

        {items.length === 0 && <p className="text-sm text-[#8A7A7A] italic">No items yet — add one above.</p>}

        <ul className="space-y-3">
          {items.map((row, i) => (
            <li key={i} className={`grid grid-cols-1 md:grid-cols-[150px_1fr_auto] gap-3 items-start p-3 rounded-lg border ${row.key ? 'border-[#C8A951]/40 bg-[#FFFDF6]' : 'border-[#E5E0D8]'}`} data-testid={`schedule-row-${i}`}>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Time</label>
                <Input value={row.time || ''} onChange={(e) => setItem(i, 'time', e.target.value)} placeholder="e.g. 3:00 PM" data-testid={`schedule-time-${i}`} />
              </div>
              <div className="space-y-2">
                {row.key && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#8A7A7A]" data-testid={`schedule-sync-badge-${row.key}`}>
                    <Link2 className="w-3 h-3 text-[#C8A951]" />
                    Synced with Wedding Day Details • {row.key}
                  </span>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Event</label>
                  <Input value={row.event || ''} onChange={(e) => setItem(i, 'event', e.target.value)} placeholder="Ceremony Begins" data-testid={`schedule-event-${i}`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Description</label>
                  <Textarea rows={2} value={row.description || ''} onChange={(e) => setItem(i, 'description', e.target.value)} data-testid={`schedule-desc-${i}`} />
                </div>
              </div>
              <div className="flex md:flex-col items-center gap-1 md:pt-6">
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                  data-testid={`schedule-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1}
                  className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                  data-testid={`schedule-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => remove(i)}
                  disabled={!!row.key}
                  title={row.key ? 'Linked to Wedding Day Details — cannot delete' : 'Delete row'}
                  className="p-1.5 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  data-testid={`schedule-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Schedule to defaults?</DialogTitle>
            <DialogDescription>This restores the original 7-item timeline. Cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="schedule-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminScheduleManager;
